import { solid, regular,brands } from '@fortawesome/fontawesome-svg-core/import.macro'
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Col, Container, Row } from 'react-bootstrap';
import { JobPosting, WithContext } from "schema-dts";
import { JsonLd } from "react-schemaorg";
import 'react-perfect-scrollbar/dist/css/styles.css';
import ReactToPrint from 'react-to-print';
import LocalizedStrings from 'react-localization';
import { Helmet } from "react-helmet";

import { JobService,Colors, EmploymentType, getEmploymentTypeAsString, Job, SalaryUnit, getSalaryUnitAsString, ClientLanguage, getClientLanguageAsString, JobList } from '../Api'; 
import { CustomButton, CustomLink, JobTitle, DetailsJobSubtitle } from './StyledComponents';
import { IconWithText, IconWithTextRight } from './IconWithText';
import { Legals } from './Legals';
import { LogoImage } from './LogoImage';
import { Preview } from './Preview';
import React, { useContext,useEffect, useRef,useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SettingsContext from '../SettingsContext';
import { useParams,useLocation } from "react-router";
import { LoadingStates } from './Enums';

let locStrings = new LocalizedStrings({
    de: {
        applyNow: "Jetzt bewerben",
        footerPrint: "Drucken",

        metaDescriptionJobPosting: "Stellenausschreibung",

        applyViaContactInformation: "Senden Sie Ihre Bewerbung bitte an Nancy Schröter von unserer Personalabteilung unter Verwendung der E-Mail-Adresse bewerbungen.berlin@wilhelmreuss.de."
    },
    en: {
        applyNow: "Apply now",
        footerPrint: "Print", 

        metaDescriptionJobPosting: "Job posting",

        applyViaContactInformation: "Please send your application to Nancy Schröter from our HR department using the e-mail address bewerbungen.berlin@wilhelmreuss.de."
    }
});

interface JobDetailsViewProps {
    job: Job;
    colors: Colors;
    logoUrl: string;
    headerUrl: string | undefined;
    listId: string;
    fontFamily: string | undefined;
    onApplyClick: any;
    legalNoticeUrl: string;
    dataSecurityUrl: string;
    previewMode: boolean;
    formalAddressPronoun: boolean;
    subtitleOnJobPosting: boolean;
    customlink1:string;
    customlink2:string;
    customlink3:string;
    customlink4:string;
    customlink5:string;
    customForm:string;
    directIntegrationIframe:boolean;
    submitApplicationSuccessHeader:string;
    submitApplicationText:string;
    clientLanguage: ClientLanguage;
    supportUrl:string;
    supportEmail:string;
    supportPhoneNumber:string;
    supportCustomMessage:string;
}

export const JobDetailsView = React.forwardRef((props: JobDetailsViewProps, ref) => {

    setLanguage(); 

    const printRef = useRef<HTMLDivElement>(null);
    const [loadingState, setLoadingState] = useState<LoadingStates>(LoadingStates.Pending)
    const { pathname } = useLocation();
    let { tenant = "", listName } = useParams();
    tenant = tenant === "" ? pathname.split('/')[1] : tenant; 
    const [jobList, setJobList] = useState<JobList>()
    const { settings } = useContext(SettingsContext);
    
    useEffect(() => {
        if (tenant && listName) {
            JobService.getJobs(tenant, listName, true).then(result => {
                if (result) {
                    setJobList(result);
               }
            }).catch(() => {
                setLoadingState(LoadingStates.Fail)
            });
            document.title = props.job.title;
        }
        
    }, [listName, props.job.title, tenant]);


    function changeFavicon():void {
        const link = document.getElementById('favicon');
        link?.setAttribute('href',settings.imagesUrl+ "/" + props.job.tenant + "." + jobList?.favIconKey);
    }

    function showSalary(): boolean {
        return props.job.salaryMin && props.job.salaryMax ? true : false
    }

    var salaryMin
    if (props.job.salaryCurrency) {
        salaryMin = props.job.salaryMin.toLocaleString('de-DE', { style: 'currency', currency: props.job.salaryCurrency, minimumFractionDigits: 0 })
    }

    var salaryMax
    if (props.job.salaryCurrency) {
        salaryMax = props.job.salaryMax.toLocaleString('de-DE', { style: 'currency', currency: props.job.salaryCurrency, minimumFractionDigits: 0 })
    }

    function getSalaryUnit(): string {
        if (props.job.salaryUnit)
            return " (" + getSalaryUnitAsString(props.job.salaryUnit, props.clientLanguage) + ")";
        else
            return "";
    }

    function getClientLanguage(): string { 
        return getClientLanguageAsString(props.clientLanguage); 
    } 
    
    function setLanguage(): void {
        switch(props.clientLanguage) { 
            case ClientLanguage.German: { 
               locStrings.setLanguage('de');
               break; 
            } 
            case ClientLanguage.English: { 
               locStrings.setLanguage('en'); 
               break; 
            } 
            // Otherwise "props.clientLanguage" is "ClientLanguage.BrowserSetting" and language used is browser setting dependent.
        } 
    }

    function getJobHeatColor(): string {
        if (props.job.applicationCount <= 5)
            return "green";
        else
            if (props.job.applicationCount <= 20)
                return "orange";
            else
                return "red";
    }

    function getApplicationCountText(): string {
        var publishFrom = new Date(props.job.publishFrom);
        var applicationText = "Bewerbungen";
        if (props.job.applicationCount === 1) {
            applicationText = "Bewerbung";
        }
        return props.job.applicationCount.toString() + " " + applicationText + " seit " + publishFrom.toLocaleDateString();
    }

    function getJobHeatIcon(): IconProp {
        if (props.job.applicationCount <= 5)
            return solid("temperature-empty");
        else
            if (props.job.applicationCount <= 20)
                return solid("temperature-half");
            else
                return solid("temperature-full");
    }

    function getJobPostingScript(job: Job): WithContext<JobPosting> {
        var context: WithContext<JobPosting> = {
            '@type': 'JobPosting',
            '@context': 'https://schema.org',
            title: job.title,
            description: job.description,
            datePosted: job.publishFrom.toLocaleString(),
            validThrough: job.publishUntil.toLocaleString(),
            employmentType: job.employmentTypesGoogle,
            hiringOrganization: {
                '@type': 'Organization',
                name: job.hiringOrganization.name,
                sameAs: job.hiringOrganization.sameAs,
            },
            jobLocation: {
                '@type': 'Place',
                address: {
                    '@type': 'PostalAddress',
                    streetAddress: job.postalAddress.streetAddress,
                    addressLocality: job.postalAddress.addressLocality,
                    addressRegion: job.postalAddress.addressRegion,
                    postalCode: job.postalAddress.postalCode,
                    addressCountry: job.postalAddress.addressCountry,
                }
            },
            baseSalary: {
                '@type': 'MonetaryAmount',
                currency: job.salaryCurrency,
                value: {
                    '@type': 'QuantitativeValue',
                    value: job.salaryMax.toLocaleString('de-DE', { minimumFractionDigits: 2 }),
                    unitText: getGoogleForJobSalaryUnit(job.salaryUnit)
                }
            }
        }
        return context
    }

    function canCreateGoogleForJobScript(job: Job): boolean {
        return !isEmpty(job.title)
            && !isEmpty(job.description)
            && job.hiringOrganization != undefined
            && !isEmpty(job.hiringOrganization.name)
            && !isEmpty(job.hiringOrganization.sameAs)
            && job.postalAddress != undefined
            && !isEmpty(job.postalAddress.addressCountry)
            && !isEmpty(job.postalAddress.addressLocality)
            && !isEmpty(job.postalAddress.postalCode)
            && !isEmpty(job.postalAddress.streetAddress)
    }

    function isEmpty(value: string): boolean {
        return value == undefined || value.length === 0
    }

    function getGoogleForJobSalaryUnit(type: SalaryUnit): string {
        if (type === undefined) return ""
        if (type === SalaryUnit.Hour) return "HOUR"
        if (type === SalaryUnit.Day) return "DAY"
        if (type === SalaryUnit.Week) return "WEEK"
        if (type === SalaryUnit.Month) return "MONTH"
        if (type === SalaryUnit.Year) return "YEAR"
        return "MONTH"
    }

    function getEmploymentTypesAsText(types: EmploymentType[]): string {
        var result: string = ""

        for (let i = 0; i < types.length; i++) {
            const typeAsString = getEmploymentTypeAsString(types[i], props.clientLanguage)

            if (result.length > 0) {
                result += ` / ${typeAsString}`
            } else {
                result = typeAsString
            }
        }

        return result
    }

    function display() {
        window.print();
    }

    const onClick = (event: React.MouseEvent) => {
        props.onApplyClick(props.job.id);
    }

    return (
        <>
            {canCreateGoogleForJobScript(props.job) && <JsonLd<JobPosting> item={getJobPostingScript(props.job)} />}
            {setLanguage()}
            {changeFavicon()}

            <Helmet>
                <title>{props.job.metaTitle !== '' ? props.job.metaTitle : props.job.title}</title>
                <meta 
                    name="description"
                    content={props.job.metaDescription !== '' ? props.job.metaDescription : locStrings.metaDescriptionJobPosting}
                />
                <meta 
                    name="keywords"
                    content={props.job.metaKeywords}
                />
                {/* <meta 
                    name="URL"
                    content={props.job.metaURL}
                /> */}
                <meta 
                    name="robots"
                    content={props.job.metaRobotsTag}
                />
                <meta 
                    name="author"
                    content={props.job.metaAuthor}
                />
                <meta 
                    name="publisher"
                    content={props.job.metaPublisher}
                />
                <link rel="canonical" href={props.job.metaCanonical} />
                {/* <html lang={props.job.metaLang !== '' ? props.job.metaLang : "de-de"}></html> */}
                <html lang="de-de"></html>
            </Helmet>

            <div ref={printRef} style={{ fontFamily: props.fontFamily }}>

                {props.headerUrl &&
                    <Row className={"ListDetailsHeader"} style={{maxHeight: "300px" }}>
                        <img src={props.headerUrl} alt="header" style={{  maxHeight: "300px" }}/>
                    </Row> }
                    <Row style={{ marginTop: "30px"}}>
                        {!props.headerUrl && < Col style={{ display: "flex", justifyContent: "left" }}>
                            <LogoImage url={props.job?.hiringOrganization?.sameAs} listId={props.listId} logoUrl={props.logoUrl} />
                        </Col>}
                        <Col style={{ display: "flex", justifyContent: "right" }}>
                            {props.customlink1?.length >0 &&<a className={"ListDetailsCustomLink1"} href={props.customlink1} target="_blank"  rel="noreferrer noopener" >
                            <span><FontAwesomeIcon icon={solid('home')} size='2x' border  /></span>
                            </a> }
                            {props.customlink2?.length >0 && <a className={"ListDetailsCustomLink2"} href={props.customlink2} target="_blank"  rel="noreferrer noopener">
                            <span><FontAwesomeIcon icon={brands('facebook-square')} size='2x' border/></span>
                            </a> }
                            {props.customlink3?.length >0 &&<a className={"ListDetailsCustomLink3"} href={props.customlink3} target="_blank"  rel="noreferrer noopener">
                                 <span><FontAwesomeIcon icon={brands('linkedin')} size='2x' border/></span>
                            </a>} 
                            {props.customlink4?.length >0 &&<a className={"ListDetailsCustomLink4"} href={props.customlink4} target="_blank"  rel="noreferrer noopener">
                            <span><FontAwesomeIcon icon={brands('twitter-square')} size='2x' border/></span>
                            </a>} 
                            {props.customlink5?.length >0 &&<a className={"ListDetailsCustomLink5"} href={props.customlink5} target="_blank"  rel="noreferrer noopener">
                            <span><FontAwesomeIcon icon={brands('square-xing')} size='2x' border/></span>
                            </a>} 
                        </Col>
                    </Row>
                <div style={{ display: "flex", justifyContent: "right" }}>
                    {false && props.job.applicationCount > 0 && <IconWithTextRight text={getApplicationCountText()} color={getJobHeatColor()} icon={getJobHeatIcon()} />}
                </div>
                <JobTitle className={"ListDetailsJobTitle"}>
                    {props.job.title}
                    {props.previewMode && <Preview clientLanguage={props.clientLanguage}/>}
                </JobTitle>
                {props.subtitleOnJobPosting && <DetailsJobSubtitle className={"ListDetailsJobSubtitle"}>
                    {props.job.subtitle}
                </DetailsJobSubtitle>}
                <div className={"ListDetailsText"} style={{ paddingBottom: "20px", display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
                    {props.job.locations?.length > 0 && <IconWithText text={props.job.locations.sort().join(", ")} color={props.colors.primary} icon={solid('map-marker')} />}
                    {props.job.employmentTypes.length !== 0 && <IconWithText text={getEmploymentTypesAsText(props.job.employmentTypes)} color={props.colors.primary} icon={regular('clock')} />}
                    {props.job.experienceLevels?.length>0  && <IconWithText text={props.job.experienceLevels.sort().join(", ")} color={props.colors.primary} icon={regular('user')} />}
                    {showSalary() && <IconWithText text={salaryMin + " - " + salaryMax + getSalaryUnit()} color={props.colors.primary} icon={solid('money-bill-wave')} />}
                </div>
                {(() => {
                    const customForm = props.job.customForm !== "" ? props.job.customForm : props.customForm;
                    if (!(props.job.tenant.toLowerCase() === 'wilhelmreuss' && customForm.toLowerCase() === 'nur anzeige')) {
                        return  <div className={"ListDetailsButton"} style={{ paddingBottom: "30px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                    <CustomButton  onClick={onClick} variant="primary" colors={props.colors}>{locStrings.applyNow}</CustomButton>
                                </div>
                    }
                    if (props.job.tenant.toLowerCase() === 'wilhelmreuss' && customForm.toLowerCase() === 'nur anzeige') {
                        // return <> {locStrings.applyNow} </>
                        return  <div style={{ paddingTop: "10px", paddingBottom: "30px", paddingLeft: "80px", paddingRight: "80px", display: "flex", justifyContent: "center" }}>
                                    {locStrings.applyViaContactInformation}
                                </div>
                    }
                })()}
                <Row><span className={"ListDetailsDescription"} dangerouslySetInnerHTML={{ __html: props.job.description }} /></Row>
                {/* <div>
                    {<small><b>Debug info: ClientLanguage: </b> {getClientLanguage()}</small>}
                </div> */}
                <footer  style={{ display: "table", margin: "auto", paddingTop: "20px" }}>
                    <Legals colors={props.colors} legalNoticeUrl={props.legalNoticeUrl} dataSecurityUrl={props.dataSecurityUrl} clientLanguage={props.clientLanguage}/>
                    <>|</>
                    <CustomLink className={"ListDetailsLinkPrint"} onClick={display} variant="primary" colors={props.colors} size='sm' >{locStrings.footerPrint}</CustomLink>
                </footer>
            </div>
        </>
    )
})
